import React from 'react';
import Highcharts from 'utils/highchartsExporting';
import HighchartsReact from 'highcharts-react-official';
import highchartsHeatmap from 'highcharts/modules/heatmap';
import PropTypes from 'prop-types';
import { HEATMAP_CELL_BORDER_COLOR, HEATMAP_EMPTY_CELL_COLOR } from '../../../customDashboards/heatmap/heatmapOptions';

if (typeof Highcharts === 'object' && !Highcharts.seriesTypes?.heatmap) {
    highchartsHeatmap(Highcharts);
}

const HeatmapChart = ({
    title = '',
    xCategories,
    yCategories,
    data,
    colorAxis,
    defaultColor = undefined,
    showValues = true,
    xAxisTitle = '',
    yAxisTitle = '',
    valueFormat,
    height = null,
    width = null,
    chartRef = null,
}) => {
    const { prefix = '', suffix = '', precision = 0, displayName = 'Value' } = valueFormat || {};
    const colorMode = colorAxis && colorAxis.dataClasses ? 'brackets' : 'gradient';

    return (
        <HighchartsReact
            key={colorMode}
            ref={chartRef}
            highcharts={Highcharts}
            options={{
                chart: { type: 'heatmap', height, width, plotBorderWidth: 1 },
                title: { text: title },
                xAxis: { categories: xCategories, title: { text: xAxisTitle } },
                yAxis: { categories: yCategories, title: { text: yAxisTitle }, reversed: true },
                colorAxis,
                legend: {
                    align: 'center',
                    layout: 'horizontal',
                    verticalAlign: 'bottom',
                    margin: 8,
                    symbolHeight: 12,
                },
                tooltip: {
                    headerFormat: '',
                    pointFormat:
                        `${xAxisTitle}: <b>{point.xLabel}</b><br/>` +
                        `${yAxisTitle}: <b>{point.yLabel}</b><br/>` +
                        `${displayName}: <b>${prefix}{point.value:,.${precision}f}${suffix}</b>`,
                    followPointer: true,
                },
                series: [
                    {
                        name: displayName,
                        borderWidth: 1,
                        borderColor: HEATMAP_CELL_BORDER_COLOR,
                        nullColor: HEATMAP_EMPTY_CELL_COLOR,
                        color: defaultColor,
                        data: data.map(([x, y, value]) => ({
                            x,
                            y,
                            value,
                            xLabel: xCategories[x],
                            yLabel: yCategories[y],
                        })),
                        dataLabels: {
                            enabled: showValues,
                            format: `${prefix}{point.value:,.${precision}f}${suffix}`,
                            // Compact font reduces (doesn't eliminate) long-value spill in dense cells.
                            style: { fontSize: '10px' },
                        },
                    },
                ],
                credits: { enabled: false },
            }}
        />
    );
};

HeatmapChart.propTypes = {
    title: PropTypes.string,
    xCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
    yCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
    data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    colorAxis: PropTypes.shape({ dataClasses: PropTypes.arrayOf(PropTypes.shape({})) }).isRequired,
    defaultColor: PropTypes.string,
    showValues: PropTypes.bool,
    xAxisTitle: PropTypes.string,
    yAxisTitle: PropTypes.string,
    valueFormat: PropTypes.shape({
        prefix: PropTypes.string,
        suffix: PropTypes.string,
        precision: PropTypes.number,
        displayName: PropTypes.string,
    }),
    height: PropTypes.number,
    width: PropTypes.number,
    chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Object) })]),
};

export default HeatmapChart;
